import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, X, Sparkles, Loader2, Download, Table } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import AppShell from '@/components/AppShell';
import PrintHeader from '@/components/PrintHeader';
import PrintReadyMarker from '@/components/PrintReadyMarker';
import PdfCoverDialog from '@/components/PdfCoverDialog';
import { useDownloadPdf } from '@/hooks/useDownloadPdf';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SaveToLibraryButton from '@/components/library/SaveToLibraryButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RubricGenerator() {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const assignmentContext = location.state;

  const [rubrics, setRubrics] = useState([]);
  const [selectedRubric, setSelectedRubric] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [unit, setUnit] = useState(null);

  // Generation form
  const [formData, setFormData] = useState({
    assessment_type: assignmentContext?.assessment_type || 'project',
    assignment_title: assignmentContext?.assignment_title || '',
    description: assignmentContext?.description || '',
    grade_level: assignmentContext?.grade_level || 9,
    criteria_count: 4
  });

  useEffect(() => {
    if (unitId) {
      fetchRubrics();
      fetchUnit();
    }
  }, [unitId]);

  const fetchUnit = async () => {
    try {
      const res = await axios.get(`${API}/units/${unitId}`);
      setUnit(res.data);
      if (res.data?.grade_level) {
        setFormData(prev => ({ ...prev, grade_level: res.data.grade_level }));
      }
    } catch (err) {
      // Non-fatal — print header will just show the form's grade
      console.warn('RubricGenerator: failed to fetch unit', err);
    }
  };

  const fetchRubrics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/rubrics`, {
        params: { unit_id: unitId }
      });
      setRubrics(response.data);
      if (response.data.length > 0) {
        setSelectedRubric(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to load rubrics');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!formData.assignment_title) {
      toast.error('Please provide an assignment title');
      return;
    }

    try {
      setGenerating(true);
      const response = await axios.post(`${API}/rubrics/generate`, {
        ...formData,
        unit_id: unitId
      });

      setRubrics([response.data, ...rubrics]);
      setSelectedRubric(response.data);
      toast.success('Rubric generated successfully!');
    } catch (error) {
      toast.error('Failed to generate rubric');
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = async (rubricId) => {
    try {
      const response = await axios.get(`${API}/rubrics/${rubricId}/export`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rubric_${selectedRubric.title.replace(/ /g, '_')}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Rubric exported!');
    } catch (error) {
      toast.error('Failed to export rubric');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const { downloading: downloadingPdf, downloadPdf } = useDownloadPdf();
  const [coverOpen, setCoverOpen] = useState(false);
  const handleDownloadPdf = () => {
    if (!unitId) return;
    setCoverOpen(true);
  };
  const handleCoverConfirm = async (cover) => {
    const filename = `rubric-${(selectedRubric?.title || unit?.title || 'rubric').replace(/[^a-z0-9-]+/gi, '-').toLowerCase().slice(0, 40)}.pdf`;
    await downloadPdf({
      resource: 'rubric',
      resourceId: unitId,
      filename,
      cover: {
        ...cover,
        title: selectedRubric?.title || unit?.title,
        grade: (unit?.grade_level ?? formData.grade_level) ? String(unit?.grade_level ?? formData.grade_level) : '',
        unit_number: unit?.unit_number != null ? String(unit.unit_number) : '',
        resource_label: 'Rubric',
        subtitle: unit?.title && selectedRubric?.title && unit.title !== selectedRubric.title ? unit.title : '',
      },
    });
    setCoverOpen(false);
  };

  if (loading && unitId) {
    return (
      <AppShell>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#14213D] border-t-transparent"></div>
            <p className="mt-4 text-[#5A6577] text-sm">Loading rubrics…</p>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10 print:max-w-none print:px-0 print:py-2">
        <PrintReadyMarker ready={!loading} />
        <PrintHeader
          resourceType="Rubric"
          resourceTitle={selectedRubric?.title}
          grade={unit?.grade_level ?? formData.grade_level}
          unitNumber={unit?.unit_number}
          unitTitle={unit?.title}
        />
        <div className="mb-8 flex items-center gap-3 print:hidden">
          <Table className="h-6 w-6 text-[#D4AF37]" />
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">AI Tools</p>
            <h1 className="font-serif-display text-3xl text-[#14213D] tracking-tight">Rubric Generator</h1>
            <p className="text-sm text-[#5A6577] mt-1 max-w-2xl">
              Generate detailed rubrics with AI for projects, essays, and presentations.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 print:grid-cols-1 print:gap-0">
          {/* Left Column: Generation Form */}
          <div className="space-y-6 print:hidden">
            <Card className="p-6">
              <h3 className="text-xl font-serif font-normal text-stone-900 mb-4">
                Generate New Rubric
              </h3>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="assessment_type">Assessment Type *</Label>
                  <Select
                    value={formData.assessment_type}
                    onValueChange={(val) => setFormData({ ...formData, assessment_type: val })}
                  >
                    <SelectTrigger id="assessment_type" data-testid="assessment-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="project">Project</SelectItem>
                      <SelectItem value="essay">Essay</SelectItem>
                      <SelectItem value="presentation">Presentation</SelectItem>
                      <SelectItem value="discussion">Discussion</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="assignment_title">Assignment Title *</Label>
                  <Input
                    id="assignment_title"
                    value={formData.assignment_title}
                    onChange={(e) => setFormData({ ...formData, assignment_title: e.target.value })}
                    placeholder="e.g., Documentary Film Project"
                    data-testid="assignment-title-input"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of the assignment..."
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="grade_level">Grade Level</Label>
                    <Select
                      value={formData.grade_level.toString()}
                      onValueChange={(val) => setFormData({ ...formData, grade_level: parseInt(val) })}
                    >
                      <SelectTrigger id="grade_level">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="9">Grade 9</SelectItem>
                        <SelectItem value="10">Grade 10</SelectItem>
                        <SelectItem value="11">Grade 11</SelectItem>
                        <SelectItem value="12">Grade 12</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="criteria_count">Criteria Count</Label>
                    <Select
                      value={formData.criteria_count.toString()}
                      onValueChange={(val) => setFormData({ ...formData, criteria_count: parseInt(val) })}
                    >
                      <SelectTrigger id="criteria_count">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">3 Criteria</SelectItem>
                        <SelectItem value="4">4 Criteria</SelectItem>
                        <SelectItem value="5">5 Criteria</SelectItem>
                        <SelectItem value="6">6 Criteria</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="w-full btn-primary"
                  data-testid="generate-rubric-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Generate Rubric
                    </>
                  )}
                </Button>
              </div>
            </Card>

            {/* Previous Rubrics List */}
            {rubrics.length > 0 && (
              <Card className="p-6">
                <h3 className="font-semibold text-stone-900 mb-4">Saved Rubrics</h3>
                <div className="space-y-2">
                  {rubrics.map((rubric) => (
                    <div
                      key={rubric.id}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedRubric?.id === rubric.id
                          ? 'bg-stone-100 border-2 border-slate-700'
                          : 'bg-stone-50 border border-stone-200 hover:border-slate-400'
                      }`}
                      onClick={() => setSelectedRubric(rubric)}
                      data-testid={`rubric-${rubric.id}`}
                    >
                      <p className="font-medium text-stone-900 text-sm">{rubric.title}</p>
                      <p className="text-xs text-stone-500 mt-1">
                        {rubric.criteria?.length || 0} criteria • {rubric.total_points} points
                      </p>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>

          {/* Right Column: Rubric Display */}
          <div>
            {selectedRubric ? (
              <Card className="p-8 print-friendly print:p-0 print:shadow-none print:border-0" data-print-block>
                <div className="flex items-center justify-between mb-6 no-print">
                  <h3 className="text-2xl font-serif font-normal text-stone-900">
                    {selectedRubric.title}
                  </h3>
                  <div className="flex gap-2 flex-wrap">
                    <SaveToLibraryButton
                      artifactType="rubric"
                      title={selectedRubric.title}
                      description={selectedRubric.description || ''}
                      gradeLevel={formData.grade_level}
                      unitId={unitId}
                      content={selectedRubric}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrint}
                      data-testid="print-rubric-btn"
                    >
                      Print
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadPdf}
                      disabled={downloadingPdf}
                      data-testid="download-pdf-btn"
                      title="Download this rubric as a PDF"
                    >
                      {downloadingPdf ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />PDF&hellip;</>
                      ) : (
                        <><Download className="h-4 w-4 mr-2" />PDF</>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleExport(selectedRubric.id)}
                      data-testid="export-rubric-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                  </div>
                </div>

                <p className="text-stone-600 mb-6">{selectedRubric.description}</p>

                <div className="space-y-6">
                  {selectedRubric.criteria?.map((criterion, idx) => (
                    <div key={`crit-${idx}-${criterion.name?.slice(0, 30) || ''}`} className="border border-stone-200 rounded-lg overflow-hidden">
                      <div className="bg-stone-100 p-3">
                        <h4 className="font-semibold text-stone-900">{criterion.name}</h4>
                      </div>
                      <div className="divide-y divide-stone-200">
                        {criterion.levels?.map((level, levelIdx) => (
                          <div key={`lvl-${levelIdx}-${level.score}`} className="p-4 flex gap-4">
                            <div className="flex-shrink-0">
                              <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                                <span className="font-bold text-slate-700">{level.score}</span>
                              </div>
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-stone-900 mb-1">{level.label}</p>
                              <p className="text-sm text-stone-700">{level.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="font-semibold text-stone-900">
                    Total Points: {selectedRubric.total_points}
                  </p>
                </div>
              </Card>
            ) : (
              <Card className="p-12 text-center">
                <Table className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                <h3 className="text-xl font-serif text-stone-900 mb-2">
                  No Rubric Selected
                </h3>
                <p className="text-stone-600">
                  Generate a new rubric or select an existing one
                </p>
              </Card>
            )}
          </div>
        </div>
      </div>
      <PdfCoverDialog
        open={coverOpen}
        onOpenChange={setCoverOpen}
        onConfirm={handleCoverConfirm}
        downloading={downloadingPdf}
        context={{
          title: selectedRubric?.title || unit?.title,
          resource_label: 'Rubric',
          grade: unit?.grade_level ?? formData.grade_level,
          unit_number: unit?.unit_number,
        }}
      />
    </AppShell>
  );
}
